/**
 * Form80 — the dedicated, SEPARATE Form 80 tab (Tzvi ruling 2026-07-21).
 *
 * Everything derivable from the pre-lodgement intake is pre-filled live
 * (read_only mirror fields, recomputed on every load and never persisted);
 * seed-once repeaters are copied into the F80 row when it is first created;
 * the client answers only the genuine Form-80-only gaps. Staff-only parts
 * (proposed travel, addresses in Australia, sponsoring employer, declaration,
 * additional info) are client_hidden. The client always sees how far through
 * the Form 80 they are, even when nothing is left for them to answer.
 *
 * Storage: its own intake_forms row (form_code 'F80-applicant') per applicant,
 * answers in intake_field_data keyed by F80 canonical names. The primary
 * intake's bootstrap is pinned to its own form_code so the two rows never
 * shadow each other.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { ArrowLeft, ArrowRight, Check, Lock, Loader2, CircleAlert } from 'lucide-react';

import { supabase } from '../lib/supabase';
import { upsertIntakeFieldData, type IntakeFieldRow } from '../lib/intakeFieldWrites';
import { useAuth } from '../hooks/useAuth';
import { useMatter } from '../hooks/useMatter';
import { SchemaRenderer } from '../lib/schema-renderer';
import type { FormSchema, SectionDef } from '../lib/schema-renderer';
import {
  loadSchema,
  formStateToRows,
  rowsToFormState,
  expandFormState,
  computeSectionProgress,
  stripClientHidden,
} from '../lib/schema-runtime';
import type { FormState } from '../lib/schema-runtime';
import {
  buildF80Prefill,
  buildF80Seeds,
  collectReadOnlyCanonicals,
  pruneReadOnlyPaths,
  applyF80Mirror,
} from '../lib/schema-runtime/f80Prefill';
import { F80_FORM_CODE, SUPPLEMENTARY_FORM_CODES_SQL } from '../lib/formCodes';
import { createSerializedWriteQueue } from '../lib/serializedWriteQueue';

const DEBOUNCE_MS = 1500;
const F80_FORM_ID = 'F80_character';

/**
 * Subclasses the Form 80 tab is offered to. 461 is in F80_character.json's
 * applicable list but is EXCLUDED here: f80Prefill maps 47SP-family
 * canonicals only, and the 147 form uses different character-battery names,
 * so a 461 client would see locked, permanently empty required fields.
 * Add 461 back only once f80Prefill carries a 147 source map.
 */
const F80_VISAS = new Set(['820', '801', '309', '100', '300']);

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Drop schema constructs the tab cannot render: the two legacy `type:
 * "section"` placeholder fields (partner details, recalled passport details).
 * The PDF path keeps them; staff complete those parts on the working copy.
 */
function sanitiseForTab(schema: FormSchema): FormSchema {
  return {
    ...schema,
    sections: schema.sections.map((s) => ({
      ...s,
      fields: (s.fields ?? []).filter((f) => (f.type as string) !== 'section'),
    })),
  };
}

function singleSectionSchema(fullSchema: FormSchema, sectionId: string): FormSchema {
  const section = fullSchema.sections.find((s) => s.section_id === sectionId);
  return { ...fullSchema, sections: section ? [section] : [] };
}

export default function Form80() {
  const { user } = useAuth();
  const { activeMatter, contact, isStaff, impersonatedMatterId, impersonatedRole, effectiveContactId } =
    useMatter();

  const audience: 'client' | 'staff' = isStaff ? 'staff' : 'client';

  const isSponsor = impersonatedMatterId
    ? impersonatedRole === 'sponsor'
    : contact?.contact_type === 'sponsor';

  // BUG-1 (Jane 2026-07-23): impersonation preview is strictly read-only.
  const isPreview = !!impersonatedMatterId;

  // --- Schema -----------------------------------------------------------------
  const [rawSchema, setRawSchema] = useState<FormSchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  useEffect(() => {
    loadSchema(F80_FORM_ID)
      .then((s) => setRawSchema(sanitiseForTab(s)))
      .catch((err: unknown) =>
        setSchemaError(err instanceof Error ? err.message : 'Failed to load the Form 80 schema.'),
      );
  }, []);

  // The audience-appropriate schema: clients never see staff-only parts.
  const schema = useMemo(() => {
    if (!rawSchema) return null;
    if (isStaff) return rawSchema;
    return { ...rawSchema, sections: stripClientHidden(rawSchema.sections) };
  }, [rawSchema, isStaff]);

  const readOnlyKeys = useMemo(
    () => (rawSchema ? collectReadOnlyCanonicals(rawSchema) : []),
    [rawSchema],
  );

  // --- Bootstrap ----------------------------------------------------------------
  const methods = useForm<FormState>({ defaultValues: {}, mode: 'onBlur' });
  const { watch, getValues, reset } = methods;

  const [f80FormId, setF80FormId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const userId = user?.id ?? null;
  const matterId = activeMatter?.id ?? null;

  // Staff viewing without impersonation manage the APPLICANT's Form 80 row
  // (the row is always bound to the applicant contact; the DB trigger forbids
  // staff-owned intake rows).
  const staffViewingClientForm = isStaff && !impersonatedMatterId;
  const f80ContactId = staffViewingClientForm
    ? (activeMatter?.primary_contact_id ?? null)
    : effectiveContactId;

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!userId || !matterId || !f80ContactId || !rawSchema) return;

      try {
        // 1. The applicant's PRIMARY intake rows: the live prefill source.
        //    Supplementary rows are excluded so this always finds the real intake.
        const { data: primaryForms, error: primaryErr } = await supabase
          .from('intake_forms')
          .select('id')
          .eq('matter_id', matterId)
          .eq('contact_id', activeMatter?.primary_contact_id ?? f80ContactId)
          .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
          .order('last_saved_at', { ascending: false })
          .limit(1);
        if (primaryErr) throw primaryErr;

        let sourceState: FormState = {};
        const primaryId = primaryForms?.[0]?.id;
        if (primaryId) {
          const { data: rows, error: rowsErr } = await supabase
            .from('intake_field_data')
            .select('field_key, field_value')
            .eq('intake_form_id', primaryId);
          if (rowsErr) throw rowsErr;
          sourceState = rowsToFormState(rows ?? []);
        }

        const prefill = buildF80Prefill(sourceState);

        // 2. Find or create the F80 row.
        const { data: existing, error: findErr } = await supabase
          .from('intake_forms')
          .select('id')
          .eq('matter_id', matterId)
          .eq('contact_id', f80ContactId)
          .eq('form_code', F80_FORM_CODE)
          .order('last_saved_at', { ascending: false })
          .limit(1);
        if (findErr) throw findErr;

        let rowId = existing?.[0]?.id ?? null;
        if (!rowId && isPreview) {
          // BUG-1: preview must not create the client's F80 row (or seed it).
          if (!cancelled) {
            setF80FormId(null);
            setHydrated(true);
          }
          return;
        }
        let isNewRow = false;
        if (!rowId) {
          const { data: created, error: insertErr } = await supabase
            .from('intake_forms')
            .insert({
              matter_id: matterId,
              contact_id: f80ContactId,
              form_code: F80_FORM_CODE,
              form_version: '2026.04',
              status: 'in_progress',
            })
            .select('id')
            .single();
          if (insertErr) {
            // Unique-violation race: another tab/device created the row
            // between our find and insert. Re-fetch it instead of failing.
            if (insertErr.code === '23505') {
              const { data: raced, error: refetchErr } = await supabase
                .from('intake_forms')
                .select('id')
                .eq('matter_id', matterId)
                .eq('contact_id', f80ContactId)
                .eq('form_code', F80_FORM_CODE)
                .limit(1);
              if (refetchErr || !raced?.[0]?.id) throw insertErr;
              rowId = raced[0].id;
            } else {
              throw insertErr;
            }
          } else {
            rowId = created.id;
            isNewRow = true;
          }
        }

        // 3. Seed-once: on first creation, copy the seedable repeaters from the
        //    pre-lodgement intake into the F80 row (flat dotted rows; the blob
        //    precedence in expandFormState means a later client edit always wins).
        if (isNewRow) {
          const seedRows = formStateToRows(rowId!, buildF80Seeds(sourceState));
          if (seedRows.length > 0) {
            const { error: seedErr } = await upsertIntakeFieldData(seedRows as IntakeFieldRow[]);
            if (seedErr) throw seedErr;
          }
        }

        // 4. Load the F80 row's saved answers and hydrate: saved answers, then
        //    the live mirror on top (mirror ALWAYS wins for read_only fields).
        const { data: f80Rows, error: f80RowsErr } = await supabase
          .from('intake_field_data')
          .select('field_key, field_value')
          .eq('intake_form_id', rowId!);
        if (f80RowsErr) throw f80RowsErr;

        if (cancelled) return;

        const savedState = expandFormState(rowsToFormState(f80Rows ?? []));
        const display = applyF80Mirror(savedState, prefill);

        setF80FormId(rowId);
        reset(display);
        setHydrated(true);
      } catch (err) {
        if (cancelled) return;
        setBootError(err instanceof Error ? err.message : 'Failed to load Form 80.');
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [userId, matterId, f80ContactId, rawSchema, isPreview, reset, activeMatter?.primary_contact_id]);

  // --- Autosave (serialised, mirror fields pruned) -------------------------------
  const debounceTimer = useRef<number | null>(null);
  const lastSavedJson = useRef<string>('');
  const pendingValuesRef = useRef<FormState | null>(null);
  const writeQueueRef = useRef(createSerializedWriteQueue());
  const readOnlyKeysRef = useRef<string[]>([]);
  readOnlyKeysRef.current = readOnlyKeys;

  const flushSave = useCallback(async (fid: string, state: FormState) => {
    setSaveStatus('saving');
    try {
      // Mirror fields are never persisted: they are re-derived from the
      // pre-lodgement intake on every load, so the two forms cannot drift.
      const rows = formStateToRows(fid, pruneReadOnlyPaths(state, readOnlyKeysRef.current));
      if (rows.length === 0) {
        setSaveStatus('idle');
        return;
      }
      const { error, count } = await upsertIntakeFieldData(rows as IntakeFieldRow[]);
      if (error) throw error;
      if (count !== rows.length) {
        throw new Error(
          `Save incomplete: expected ${rows.length} field(s) to save but the server confirmed ${count ?? 0}.`,
        );
      }
      setLastSaved(new Date());
      setSaveStatus('saved');
      window.setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 2000);
    } catch (err) {
      console.error('Form80 autosave failed', err);
      setSaveStatus('error');
    }
  }, []);

  const scheduleFlush = useCallback(
    async (fid: string): Promise<void> => {
      const result = await writeQueueRef.current.schedule(async () => {
        const latest = getValues() as FormState;
        const json = JSON.stringify(latest);
        if (json === lastSavedJson.current) return null;
        await flushSave(fid, latest);
        return json;
      });
      if (!result.wasSuperseded && result.value) {
        lastSavedJson.current = result.value;
        pendingValuesRef.current = null;
      }
    },
    [flushSave, getValues],
  );

  useEffect(() => {
    if (!f80FormId || !hydrated) return;

    const subscription = watch((values) => {
      pendingValuesRef.current = values as FormState;
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
      debounceTimer.current = window.setTimeout(() => {
        void scheduleFlush(f80FormId);
      }, DEBOUNCE_MS);
    });

    const flushNow = () => {
      if (pendingValuesRef.current) void scheduleFlush(f80FormId);
    };
    window.addEventListener('beforeunload', flushNow);
    window.addEventListener('pagehide', flushNow);

    return () => {
      subscription.unsubscribe();
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
      window.removeEventListener('beforeunload', flushNow);
      window.removeEventListener('pagehide', flushNow);
      flushNow();
    };
  }, [f80FormId, hydrated, isPreview, watch, scheduleFlush]);

  // --- Progress -------------------------------------------------------------------
  const watchedValues = watch();
  const sectionProgress = useMemo(() => {
    if (!schema || !hydrated) return [];
    return computeSectionProgress(schema, watchedValues as FormState);
  }, [schema, watchedValues, hydrated]);

  const overallPct = useMemo(() => {
    let total = 0;
    let missing = 0;
    for (const s of sectionProgress) {
      total += s.totalRequired;
      missing += s.missingRequired;
    }
    if (total === 0) return 0;
    return Math.round(((total - missing) / total) * 100);
  }, [sectionProgress]);

  // --- Guards (after hooks) ----------------------------------------------------
  if (!activeMatter) return <Navigate to="/dashboard" replace />;
  if (isSponsor) return <Navigate to="/dashboard" replace />;
  if (!F80_VISAS.has(activeMatter.visa_subclass)) return <Navigate to="/dashboard" replace />;

  const sections: SectionDef[] = schema?.sections ?? [];
  const boundedIndex = Math.min(currentSectionIndex, Math.max(0, sections.length - 1));
  const currentSection = sections[boundedIndex] ?? null;

  if (schemaError || bootError) {
    return (
      <div className="card p-6 text-sm text-red-600 flex items-center gap-2">
        <CircleAlert size={16} /> {schemaError ?? bootError}
      </div>
    );
  }

  if (!schema || !hydrated) {
    return (
      <div className="card p-6 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> Loading Form 80...
      </div>
    );
  }

  // Reachable only in preview (BUG-1): every other path always has an F80 row.
  if (!f80FormId) {
    return (
      <div className="card p-6 text-sm text-slate-600">
        The client has not started their Form 80 yet.
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="space-y-4">
        <div className="card p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-lg font-semibold text-slate-800">
                Form 80: Personal particulars
              </h1>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                The Department of Home Affairs asks most partner visa applicants to
                complete Form 80. Anything you already told us in your application is
                filled in for you and shown locked. Please answer the remaining
                questions. Your case manager reviews the completed form with you, and
                nothing is lodged from this portal.
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-2xl font-semibold text-[#1A8DC7]">{overallPct}%</div>
              <div className="text-xs text-slate-500">complete</div>
              <div className="text-xs text-slate-400 mt-1">{renderSaveLabel(saveStatus, lastSaved)}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-start">
          <nav className="card p-3">
            <ul className="space-y-1">
              {sections.map((s, i) => {
                const prog = sectionProgress.find((p) => p.section_id === s.section_id);
                const active = i === boundedIndex;
                return (
                  <li key={s.section_id}>
                    <button
                      type="button"
                      onClick={() => setCurrentSectionIndex(i)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 ${
                        active ? 'bg-[#EBF7FD] text-[#1A8DC7] font-medium' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className="truncate">{s.title.replace(/^Part [A-T] - /, '')}</span>
                      {prog?.complete ? (
                        <Check size={14} className="text-emerald-500 shrink-0" />
                      ) : prog && prog.totalRequired > 0 ? (
                        <span className="text-[10px] text-slate-400 shrink-0">
                          {prog.totalRequired - prog.missingRequired}/{prog.totalRequired}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="space-y-4">
            <div className="card p-3 text-xs text-slate-500 flex items-center gap-2">
              <Lock size={12} className="shrink-0" />
              Locked fields come from your application. If something locked looks
              wrong, update it in your Intake Form or tell your case manager.
            </div>

            {/* BUG-1 (Jane 2026-07-23): in preview every field is inert. `disabled`
                kills native inputs and blur-validation; `pointer-events-none`
                (inherited) covers custom div-based widgets; `contents` keeps the
                layout untouched. */}
            <fieldset
              className="contents"
            >
            {currentSection && (
              <SchemaRenderer
                schema={singleSectionSchema(schema, currentSection.section_id)}
                formId={F80_FORM_ID}
                audience={audience}
                showLoadingState
              />
            )}
            </fieldset>

            <div className="flex items-center justify-between">
              <button
                type="button"
                className="btn-secondary flex items-center gap-1.5"
                disabled={boundedIndex === 0}
                onClick={() => setCurrentSectionIndex((i) => Math.max(0, i - 1))}
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                type="button"
                className="btn-primary flex items-center gap-1.5"
                disabled={boundedIndex >= sections.length - 1}
                onClick={() => setCurrentSectionIndex((i) => Math.min(sections.length - 1, i + 1))}
              >
                Next <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

function renderSaveLabel(status: SaveStatus, lastSaved: Date | null): string {
  if (status === 'saving') return 'Saving...';
  if (status === 'error') return 'Save failed. Will retry on next change.';
  if (lastSaved) return 'Saved';
  return 'Autosaves as you type';
}
